import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { PixPayment } from 'pages/NewCartCOD/v2'
import CreditCardPayment from 'pages/NewCartCheckout/PaymentMethod/CreditCard'
import { useEffect, useState } from 'react'
import { BillingAddressForm, type TBillingAddress } from './BillingAddressForm'
import { attachProgramOrderIfNeeded, type TProgramPaymentSummary } from './programCart'
import { PaymentMethodPicker, TrailHeading, type TProgramPaymentMethod } from './screens'

interface ProgramPaymentStepProps {
  method: TProgramPaymentMethod
  onMethod: (value: TProgramPaymentMethod) => void
  // Cobrança recusada ou que não pôde ser gerada: o fluxo mostra a tela de erro de pagamento.
  onFailed: (summary: TProgramPaymentSummary) => void
  // O pedido já está pago (ex.: a pessoa voltou ao passo depois de pagar): segue para a confirmação sem nova cobrança.
  onPaid: (summary: TProgramPaymentSummary) => void
}

const noop = async () => ({ success: 'true' })

// Pagamento sem login, no mesmo modelo do link de carrinho: o pedido é lido pelo uuid (?cart_id=) e as cobranças
// usam as rotas públicas. Pix vem do NewCartCOD v2; o cartão é o formulário do checkout público (o v2 só paga
// com cartão salvo, que exige conta logada). O retorno pós-pagamento leva de volta à agenda da trilha.
export const ProgramPaymentStep = ({ method, onMethod, onFailed, onPaid }: ProgramPaymentStepProps) => {
  const { lastOrderCreated, getLastOrder } = usePrepareCart()
  const items = lastOrderCreated?.items || []
  const [billingAddress, setBillingAddress] = useState<TBillingAddress | null>(null)

  useEffect(() => {
    if (!items.length) getLastOrder()
  }, [items.length])

  const paidPayment = (lastOrderCreated?.payments || []).find(
    (payment: { status?: string }) => payment.status === 'paid'
  )

  useEffect(() => {
    if (!paidPayment || !lastOrderCreated?.id) return undefined

    let cancelled = false
    const run = async () => {
      await attachProgramOrderIfNeeded(lastOrderCreated)
      if (cancelled) return
      onPaid({
        orderId: lastOrderCreated.id,
        method: paidPayment.method === 'pix' ? 'pix' : 'credit',
        amount: Number(lastOrderCreated.amount)
      })
    }
    run()

    return () => {
      cancelled = true
    }
  }, [paidPayment?.id, lastOrderCreated?.id])

  return (
    <div className="flex flex-1 flex-col gap-5">
      <TrailHeading eyebrow="Pagamento" title="Forma de pagamento" sub="Escolha como prefere pagar o programa." />
      {paidPayment ? (
        <p className="m-0 text-sm text-[#6B6560]">Pagamento recebido. Confirmando o seu programa…</p>
      ) : (
        <PaymentMethodPicker value={method} onChange={onMethod} />
      )}
      {!lastOrderCreated?.id && <p className="m-0 text-sm text-[#6B6560]">Carregando seu pedido…</p>}
      {lastOrderCreated?.id && !paidPayment && method === 'pix' && (
        <PixPayment selectedMethod="pix" cartItems={items} />
      )}
      {lastOrderCreated?.id && !paidPayment && method === 'credit' && (
        <>
          <BillingAddressForm onChange={setBillingAddress} />
          <CreditCardPayment
            loadingContainer={false}
            paymentMethod="credit_card"
            finishOrderByPaymentMethod={noop}
            disabledPix
            disabledBoleto
            billingAddressRequired
            embedded
            onPaymentError={({ installments }) =>
              onFailed({
                orderId: lastOrderCreated?.id,
                method: 'credit',
                amount: Number(lastOrderCreated?.amount),
                installments
              })
            }
            billingAddress={billingAddress as Record<string, string> | null}
          />
        </>
      )}
    </div>
  )
}
